import { type FC, useCallback, useRef, useState } from "react";

import { SearchableCard } from "@snapchat/snap-design-system";
import AutoSizer from "react-virtualized-auto-sizer";

import { useLensManagementRoutes } from "../../../core/routes/hooks";
import { type MyLensesLens } from "../../../gql-generated/types";
import { PageContentSpinner, SearchContainer } from "../../../layout/components";
import { LensDistributionRestrictionsModal } from "../../../lens/components/shared";
import { useSubmissionErrors } from "../../../lens/hooks";
import { VirtualizedList } from "../../../shared/components";
import { useShouldShowAdsAvailability, useTimeout, useToggleState } from "../../../shared/hooks";
import { useLensSubmissionErrorsModalState, useLensesOfSelectedContainer } from "../../hooks";
import { type LensModalProps } from "../../models";
import { LensSubmissionErrorsModal } from "../modal";

import { VirtualizedContainer, searchContainerStyle } from "./LensesList.styles";
import { LensesListHeader } from "./header";
import { LoadableLensRow, type LoadableLensRowData } from "./row";

const lensHeight = 48;

export const LensesList: FC = () => {
    const listRef = useRef<HTMLElement>(null);
    const [scrollbarWidth, setScrollbarWidth] = useState(0);
    const { goToLensPage } = useLensManagementRoutes();

    const { lenses, lensSearchText, setLensSearchText, loadMore, hasMore, isFirstFetching, isReFetching } =
        useLensesOfSelectedContainer();

    const onSelect = useCallback(
        ({ id }: MyLensesLens) => {
            goToLensPage(id);
        },
        [goToLensPage],
    );

    const [submissionErrorModalProps, showSubmissionErrorModal] = useLensSubmissionErrorsModalState();

    const [modalLens, setModalLens] = useState<MyLensesLens | null>(null);

    const { submissionErrors, loading: errorsLoading } = useSubmissionErrors(modalLens?.id);

    const [distributionRestrictionsModalProps, showDistributionRestrictionsModal] = useToggleState();

    const { loading: shouldShowAvailabilityLoading, shouldShowAdsAvailability } = useShouldShowAdsAvailability();

    const submissionErrorsProps: LensModalProps = {
        isEnabled: true,
        showModal: showSubmissionErrorModal,
        setLens: setModalLens,
    };
    const distributionRestrictionsProps: LensModalProps = {
        isEnabled: true,
        showModal: showDistributionRestrictionsModal,
        setLens: setModalLens,
    };

    const [refreshScrollbarWidth] = useTimeout(() => {
        setScrollbarWidth(listRef.current ? listRef.current.offsetWidth - listRef.current.clientWidth : 0);
    });

    if (isFirstFetching || shouldShowAvailabilityLoading) {
        return <PageContentSpinner />;
    }

    return (
        <>
            <SearchableCard
                searchPlaceholder="Search Lenses"
                padBody={false}
                onSearch={setLensSearchText}
                searchValue={lensSearchText}
                className={searchContainerStyle}
            >
                <LensesListHeader scrollbarWidth={scrollbarWidth} showAdsAvailability={shouldShowAdsAvailability} />
                <VirtualizedContainer>
                    <SearchContainer loading={isReFetching} noResults={lenses.length === 0} height={"100%"}>
                        <AutoSizer onResize={refreshScrollbarWidth}>
                            {({ height, width }) => (
                                <VirtualizedList<MyLensesLens, LoadableLensRowData>
                                    data={{
                                        list: lenses,
                                        data: {
                                            submissionErrorsProps,
                                            distributionRestrictionsProps,
                                        },
                                        onSelect,
                                    }}
                                    renderMore={loadMore}
                                    hasMore={hasMore}
                                    itemSize={lensHeight}
                                    height={height}
                                    width={width}
                                    outerRef={listRef}
                                >
                                    {LoadableLensRow}
                                </VirtualizedList>
                            )}
                        </AutoSizer>
                    </SearchContainer>
                </VirtualizedContainer>
            </SearchableCard>
            {modalLens && (
                <>
                    <LensSubmissionErrorsModal
                        modalProps={submissionErrorModalProps}
                        submissionErrors={submissionErrors}
                        errorsLoading={errorsLoading}
                    />
                    <LensDistributionRestrictionsModal
                        modalProps={distributionRestrictionsModalProps}
                        lensId={modalLens.id}
                        lensName={modalLens.name}
                    />
                </>
            )}
        </>
    );
};
